import { useCallback, useState } from "react";
import { bookings } from "../../../../services/bookingService";
import { useQuery } from "@tanstack/react-query";
import Toster from "../../../common/Toster";
import Pagination from "../../../common/Pagination";
import Dialog from "../../../dialog/Dialog";
import { AdminPageCard, AdminPageCardToolbar } from "../../../common/AdminPageLayout";
import { getTableEmptyProps } from "../../../common/supplierTableEmpty";
import {
  SupplierFilterToolbar,
  SupplierFilterField,
} from "../../../common/supplierPageUi";
import {
  AdminTable,
  AdminTableHead,
  AdminTableTh,
  AdminTableBody,
  AdminTableRow,
  AdminTableCell,
  TableSearchInput,
  TableEmptyRow,
  ViewDetailsButton,
  TableActionsCell,
} from "../../../common/adminTable";
import { getPricingFeeFields } from "../BookingPricingFees";
import {
  formatDate,
  formatDateTime,
  formatTime,
  getPaymentStatus,
  getPaymentStatusColor,
  getDaysUntilTravel,
  selectedRowClass,
} from "../bookingDetailHelpers";
import {
  BookingDetailHeader,
  BookingDetailSection,
  BookingDetailFields,
  BookingDetailTable,
  BookingDetailMetaChip,
  BookingDetailBadge,
  BookingDetailUserCard,
  BookingDetailNav,
  BookingPaymentProgress,
  CopyableText,
} from "../BookingDetailUi";

const SECTION_IDS = {
  overview: "booking-section-overview",
  payment: "booking-section-payment",
  customer: "booking-section-customer",
};

const NAV_ITEMS = [
  { id: SECTION_IDS.overview, label: "Overview", icon: "fi fi-rr-apps" },
  { id: SECTION_IDS.payment, label: "Payments", icon: "fi fi-rr-credit-card" },
  { id: SECTION_IDS.customer, label: "Customer", icon: "fi fi-rr-user" },
];

const PackageBookings = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [activeSection, setActiveSection] = useState(SECTION_IDS.overview);
  const [copyToast, setCopyToast] = useState(null);

  const { data: bookingsData } = useQuery({
    queryKey: ["packageBookings", currentPage, searchQuery],
    queryFn: bookings,
  });

  const bookingsList = bookingsData?.data?.data || [];
  const pagination = {
    currentPage: bookingsData?.data?.current_page || 1,
    lastPage: bookingsData?.data?.last_page || 1,
    perPage: bookingsData?.data?.per_page || 10,
    total: bookingsData?.data?.total || 0,
    from: bookingsData?.data?.from || 0,
    to: bookingsData?.data?.to || 0,
  };

  const handlePageChange = (page) => {
    if (page !== currentPage) {
      setCurrentPage(page);
    }
  };

  const handleViewDetails = (booking) => {
    setSelectedBookingId(booking.id);
    setActiveSection(SECTION_IDS.overview);
    setShowDetailsDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDetailsDialog(false);
    setSelectedBookingId(null);
    setActiveSection(SECTION_IDS.overview);
  };

  const handleCopied = useCallback((label, type = "success") => {
    setCopyToast({ message: `${label} copied to clipboard`, type });
    setTimeout(() => setCopyToast(null), 2500);
  }, []);

  const scrollToSection = useCallback((sectionId) => {
    setActiveSection(sectionId);
    document.getElementById(sectionId)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, []);

  const formatCurrency = (amount) => {
    return parseFloat(amount).toLocaleString("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    });
  };

  const selectedBooking = bookingsList.find(
    (booking) => booking.id === selectedBookingId
  );

  return (
    <>
      {copyToast && (
        <Toster
          message={copyToast.message}
          type={copyToast.type}
          duration={2500}
          onClose={() => setCopyToast(null)}
        />
      )}

      {showError && (
        <Toster
          message="An error occurred"
          type="error"
          duration={3000}
          onClose={() => setShowError(false)}
        />
      )}

      {showSuccess && successMessage && (
        <Toster
          message={successMessage}
          type="success"
          duration={3000}
          onClose={() => {
            setShowSuccess(false);
            setSuccessMessage("");
          }}
        />
      )}

      <AdminPageCard>
        <AdminPageCardToolbar>
          <SupplierFilterToolbar
            showReset={!!searchQuery}
            onReset={() => setSearchQuery("")}
          >
            <SupplierFilterField label="Search">
              <TableSearchInput
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search package bookings..."
                className="mt-1 w-full sm:w-full"
              />
            </SupplierFilterField>
          </SupplierFilterToolbar>
        </AdminPageCardToolbar>

        <AdminTable className="shadow-none rounded-none">
          <AdminTableHead>
            <AdminTableTh>#</AdminTableTh>
            <AdminTableTh>Package</AdminTableTh>
            <AdminTableTh>Booking Date</AdminTableTh>
            <AdminTableTh>Total Amount</AdminTableTh>
            <AdminTableTh>Paid Amount</AdminTableTh>
            <AdminTableTh>Balance</AdminTableTh>
            <AdminTableTh>Status</AdminTableTh>
            <AdminTableTh align="right">Actions</AdminTableTh>
          </AdminTableHead>
          <AdminTableBody>
            {bookingsList.length > 0 ? (
              bookingsList.map((booking, index) => (
                <AdminTableRow
                  key={booking.id}
                  className={selectedRowClass(
                    selectedBookingId === booking.id && showDetailsDialog
                  )}
                >
                  <AdminTableCell>{index + 1}</AdminTableCell>
                  <AdminTableCell>{booking.package.name}</AdminTableCell>
                  <AdminTableCell>{formatDate(booking.booking_date)}</AdminTableCell>
                  <AdminTableCell>{formatCurrency(booking.total_amount)}</AdminTableCell>
                  <AdminTableCell>{formatCurrency(booking.total_paid)}</AdminTableCell>
                  <AdminTableCell>{formatCurrency(booking.balance)}</AdminTableCell>
                  <AdminTableCell>
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ring-1 ring-inset ${getPaymentStatusColor(
                        getPaymentStatus(booking)
                      )}`}
                    >
                      {getPaymentStatus(booking)}
                    </span>
                  </AdminTableCell>
                  <TableActionsCell>
                    <ViewDetailsButton
                      onClick={() => handleViewDetails(booking)}
                      ariaLabel={`View details for booking ${booking.id}`}
                    />
                  </TableActionsCell>
                </AdminTableRow>
              ))
            ) : (
              <TableEmptyRow
                colSpan={8}
                {...getTableEmptyProps("packageBookings", {
                  hasActiveFilters: !!searchQuery,
                  onClearFilters: () => setSearchQuery(""),
                })}
              />
            )}
          </AdminTableBody>
        </AdminTable>
      </AdminPageCard>

      {showDetailsDialog && selectedBooking && (
        <Dialog
          title="Package Booking Details"
          open={showDetailsDialog}
          onClose={handleCloseDialog}
          width="w-full"
          variant="detail"
          showCloseButton={false}
        >
          <div className="space-y-5">
            <BookingDetailNav items={NAV_ITEMS} activeId={activeSection} onSelect={scrollToSection} />

            <BookingDetailHeader
              bookingId={selectedBooking.id}
              title={selectedBooking.package?.name || "Package"}
              onCopyBookingId={handleCopied}
              meta={
                <>
                  <BookingDetailMetaChip icon="fi fi-rr-calendar">
                    Travel: {formatDate(selectedBooking.booking_date)}
                  </BookingDetailMetaChip>
                  {getDaysUntilTravel(selectedBooking.booking_date) ? (
                    <BookingDetailMetaChip icon="fi fi-rr-clock">
                      {getDaysUntilTravel(selectedBooking.booking_date)}
                    </BookingDetailMetaChip>
                  ) : null}
                  <BookingDetailMetaChip icon="fi fi-rr-marker">
                    {selectedBooking.package?.starting_location || "Location N/A"}
                  </BookingDetailMetaChip>
                  <BookingDetailMetaChip icon="fi fi-rr-backpack">
                    {selectedBooking.package?.total_days || 0}D / {selectedBooking.package?.total_nights || 0}N
                  </BookingDetailMetaChip>
                </>
              }
              status={getPaymentStatus(selectedBooking)}
              statusClassName={getPaymentStatusColor(getPaymentStatus(selectedBooking))}
              amount={formatCurrency(selectedBooking.total_amount)}
              bookedAt={formatDate(selectedBooking.created_at)}
              bookedTime={formatTime(selectedBooking.created_at)}
              stats={[
                { label: "Adults", value: selectedBooking.adult_count, icon: "fi fi-rr-user" },
                { label: "Children", value: selectedBooking.child_count, icon: "fi fi-rr-users-alt" },
                { label: "Infants", value: selectedBooking.infant_count, icon: "fi fi-rr-heart" },
                {
                  label: "Balance",
                  value: formatCurrency(selectedBooking.balance),
                  highlight: parseFloat(selectedBooking.balance) > 0,
                },
              ]}
            />

            <BookingPaymentProgress
              total={selectedBooking.total_amount}
              paid={selectedBooking.total_paid}
              balance={selectedBooking.balance}
              formatCurrency={formatCurrency}
            />

            <div id={SECTION_IDS.overview} className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <BookingDetailSection title="Booking Information" icon="fi fi-rr-receipt">
                <BookingDetailFields
                  onCopied={handleCopied}
                  fields={[
                    {
                      label: "Booking ID",
                      value: `#${selectedBooking.id}`,
                      copyable: true,
                      copyValue: selectedBooking.id,
                    },
                    { label: "Travel Date", value: formatDate(selectedBooking.booking_date) },
                    {
                      label: "Total Amount",
                      value: formatCurrency(selectedBooking.total_amount),
                      highlight: true,
                    },
                    { label: "Discount", value: formatCurrency(selectedBooking.discount_amount) },
                    ...getPricingFeeFields(selectedBooking, formatCurrency, 5),
                    {
                      label: "Paid Amount",
                      value: formatCurrency(selectedBooking.total_paid),
                      highlight: parseFloat(selectedBooking.balance) === 0,
                    },
                    { label: "Balance", value: formatCurrency(selectedBooking.balance) },
                  ]}
                />
              </BookingDetailSection>

              <BookingDetailSection title="Package Details" icon="fi fi-rr-backpack">
                <BookingDetailFields
                  fields={[
                    { label: "Package Name", value: selectedBooking.package?.name || "—", fullWidth: true },
                    {
                      label: "Tour Type",
                      value: selectedBooking.package?.tour_type?.replace(/_/g, " ") || "Not specified",
                    },
                    {
                      label: "Duration",
                      value: `${selectedBooking.package?.total_days || 0} Days / ${selectedBooking.package?.total_nights || 0} Nights`,
                    },
                    {
                      label: "Group Size",
                      value: `Min ${selectedBooking.package?.minimum_members || 0} · Max ${selectedBooking.package?.maximum_members || 0}`,
                    },
                    {
                      label: "Starting Location",
                      value: selectedBooking.package?.starting_location || "Not specified",
                      fullWidth: true,
                    },
                    { label: "Pickup Point", value: selectedBooking.package?.pickup_point || "Not specified" },
                    { label: "Drop-off Point", value: selectedBooking.package?.dropoff_point || "Not specified" },
                  ]}
                />
              </BookingDetailSection>
            </div>

            <BookingDetailSection
              id={SECTION_IDS.payment}
              title="Payment History"
              icon="fi fi-rr-credit-card"
              action={
                (selectedBooking.package_payment || []).length > 0 ? (
                  <span className="text-[10px] font-medium text-gray-400">
                    {(selectedBooking.package_payment || []).length} transaction
                    {(selectedBooking.package_payment || []).length === 1 ? "" : "s"}
                  </span>
                ) : null
              }
            >
              <BookingDetailTable
                columns={["Date", "Amount", "Type", "Status", "Order ID"]}
                emptyTitle="No payments yet"
                emptyMessage="Payment records will appear here once the customer completes a transaction."
                rows={(selectedBooking.package_payment || []).map((payment) => (
                  <tr key={payment.id} className="transition hover:bg-gray-50/80">
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                      {formatDate(payment.created_at)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">
                      {formatCurrency(payment.amount)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm capitalize text-gray-600">
                      {payment.payment_type}
                    </td>
                    <td className="px-4 py-3">
                      <BookingDetailBadge
                        className={
                          payment.status === "completed"
                            ? "bg-emerald-50 text-emerald-700 ring-emerald-600/20"
                            : "bg-red-50 text-red-700 ring-red-600/20"
                        }
                      >
                        {payment.status}
                      </BookingDetailBadge>
                    </td>
                    <td className="max-w-[200px] px-4 py-3">
                      <CopyableText
                        value={payment.order_id}
                        label="Order ID"
                        onCopied={handleCopied}
                        mono
                        className="font-mono text-xs text-gray-500"
                      />
                    </td>
                  </tr>
                ))}
              />
            </BookingDetailSection>

            <BookingDetailSection id={SECTION_IDS.customer} title="User Details" icon="fi fi-rr-user">
              <BookingDetailUserCard user={selectedBooking.user} onCopied={handleCopied} />
            </BookingDetailSection>
          </div>
        </Dialog>
      )}

      <div className="mt-4">
        <Pagination pagination={pagination} onPageChange={handlePageChange} showInfo={true} />
      </div>
    </>
  );
};

export default PackageBookings;
